//Трекеры для обнволения с сервером
const syncTimers = {};
const lastStates = {};

import { getUserBriefcase } from "@/Groups/Briefcase/private/api/getUserBriefcase";
import { addAssetToBriefcase } from "@/Groups/Briefcase/private/api/addAssetToBriefcase";
import { deleteAssetFromBriefcase } from "@/Groups/Briefcase/private/api/deleteAssetFromBriefcase";
import { changeAssetIndex } from "@/Groups/Briefcase/private/api/changeAssetIndex";
import { saveAssetWeights } from "@/Groups/Briefcase/private/api/saveAssetWeights";
import { getPortfolioVisibility } from "@/Groups/Briefcase/private/api/getPortfolioVisibility";
import { patchPortfolioVisibility } from "@/Groups/Briefcase/private/api/patchPortfolioVisibility";
import { parseWeight, isValidWeight, resolveWeights, WEIGHT_STATUS } from "@/Groups/Briefcase/private/utils/resolveWeights";

// Уникальный ключ актива-объекта из поиска/pending ({api_id|secid, assetType})
function assetKey(asset) {
  return asset.assetType === 'stock_ru' ? `stock_ru:${asset.secid}` : `coin:${asset.api_id}`;
}

// Ключ элемента портфеля в серверной форме {index, coin, stock_ru}
function itemKey(item) {
  return item.coin ? `coin:${item.coin.api_id}` : `stock_ru:${item.stock_ru.secid}`;
}

// Проверяет, относится ли элемент портфеля к тому же типу, что и asset/assetType
function sameType(item, assetType) {
  return assetType === 'stock_ru' ? !!item.stock_ru : !!item.coin;
}

// Параметры API-запроса для актива-объекта из поиска
function assetParams(asset) {
  return asset.assetType === 'stock_ru' ? { stockRuId: asset.secid } : { coinId: asset.api_id };
}

// Параметры API-запроса для элемента портфеля
function itemParams(item) {
  return item.coin ? { coinId: item.coin.api_id } : { stockRuId: item.stock_ru.secid };
}

// Сохранённая доля актива (API отдаёт Decimal строкой) → число или null
function savedWeight(item) {
  return item.weight === null || item.weight === undefined ? null : Number(item.weight);
}

// Черновики долей для режима редактирования: api_id → строка из поля ввода
function draftsFromAssets(assets) {
  return Object.fromEntries(
    assets.filter(item => item.coin).map(item => [item.coin.api_id, savedWeight(item) === null ? '' : String(savedWeight(item))])
  );
}

const Briefcase = {
  namespaced: true,

  state: {
    assets: [],
    pendingAssets: [],
    isLoadingAssets: false,
    isCoinSearchOpened: false,
    isEditing: false,
    isReady: false,
    // Общая для списка портфеля и поиска вкладка ('coin'/'stock_ru') — хранится в сторе,
    // т.к. оба компонента пересоздаются при открытии/закрытии поиска (v-if в WidgetModule)
    // и теряют локальный ref при каждом переключении.
    activeAssetTab: 'coin',
    // Счётчик подтверждённых изменений портфеля — Скринер подписан на него и
    // перезагружает список при add/delete (см. ScannerPage.vue)
    assetsRevision: 0,
    draftWeights: {},
    weightsError: null,
    isSavingWeights: false,
    isPublic: false,
    isVisibilityLoaded: false,
    isSavingVisibility: false,
    visibilityError: null,
  },

  actions: {
    togglePendingAsset({ commit }, asset) {
      commit('TOGGLE_PENDING_ASSET', asset);
    },

    setActiveAssetTab({ commit }, tab) {
      commit('SET_ACTIVE_ASSET_TAB', tab);
    },

    async syncPendingAssets({ state, commit, dispatch }) {
      const assetsInBriefcase = state.assets.map(itemKey);
      const promises = [];

      for (const asset of state.pendingAssets) {
        const isInBriefcase = assetsInBriefcase.includes(assetKey(asset));
        if (!isInBriefcase) {
          promises.push(addAssetToBriefcase(assetParams(asset)));
          commit('TOGGLE_ASSET', asset);
        } else {
          promises.push(deleteAssetFromBriefcase(assetParams(asset)));
          commit('TOGGLE_ASSET', asset);
        }
      }
      dispatch('toggleSearchSection', false);
      try {
        await Promise.all(promises);
        // Инкрементим только если реально были изменения — иначе Скринер дёрнется впустую
        if (promises.length > 0) {
          commit('BUMP_ASSETS_REVISION');
        }
      } catch (err) {
        console.error('Error syncing pending assets:', err);
      }
      commit('CLEAR_PENDING_ASSETS');
    },

    toggleAsset({ commit, state }, asset) {
      const key = assetKey(asset);
      const wasInAssets = state.assets.some(el => itemKey(el) === key);
      //1.Мгновенно обновим UI
      commit('TOGGLE_ASSET', asset);
      //2.Сохраняем "последнее состояние" для сравнения позже
      lastStates[key] = !wasInAssets;
      //3.Если пользователь повторно кликает — сбросить предыдущий таймер
      if (syncTimers[key]) {
        clearTimeout(syncTimers[key].timer);
      }
      //4.Сама отправка вынесена в функцию — её же дёргает flushPendingAssetSync,
      // чтобы «Готово» не улетело раньше debounce (см. saveWeights)
      const run = async () => {
        const isNowInAssets = state.assets.some(el => itemKey(el) === key);
        //5. Если состояние изменилось по сравнению с тем, что было ДО клика — отправляем
        if (isNowInAssets !== wasInAssets) {
          try {
            (isNowInAssets)
              ? await addAssetToBriefcase(assetParams(asset))
              : await deleteAssetFromBriefcase(assetParams(asset));
            // Кэш скринера на бэке уже сброшен (on_commit) — сигналим Скринеру перезагрузиться
            commit('BUMP_ASSETS_REVISION');
          } catch (err) {
            console.error("Sync error:", err);
          }
        }
        delete syncTimers[key];
        delete lastStates[key];
      };
      //6.Установить новый debounce таймер
      syncTimers[key] = { run, timer: setTimeout(run, 1000) };
    },

    // Немедленно выполняет все отложенные add/delete-запросы по активам портфеля.
    // Нужно перед отправкой долей: иначе бэк на «Готово» может ещё видеть удалённую монету.
    async flushPendingAssetSync() {
      const pending = Object.values(syncTimers);
      pending.forEach(({ timer }) => clearTimeout(timer));
      await Promise.all(pending.map(({ run }) => run()));
    },

    toggleSearchSection({ commit }, status){
      commit('SET_SEARCH_SECTION_OPENED_FLAG', status);
    },

    async getAssets({ commit }){
      commit('SET_LOADING_FLAG', true);
      const data = await getUserBriefcase();
      commit('SET_READY_FLAG');
      commit('SET_ASSETS', data.assets);
      commit('SET_LOADING_FLAG', false);
    },

    // Переупорядочивание внутри одного типа актива (вкладки не смешиваются)
    async moveAsset({ commit, state }, { assetType, from, to }) {
      if (from === to) return;

      const typedItems = state.assets.filter(item => sameType(item, assetType));
      const otherItems = state.assets.filter(item => !sameType(item, assetType));

      const reordered = [...typedItems];
      const movedItem = reordered.splice(from, 1)[0];
      reordered.splice(to, 0, movedItem);

      const updatedIndexes = [];
      reordered.forEach((el, i) => {
        if (el.index !== i) {
          el.index = i;
          updatedIndexes.push({ ...itemParams(el), index: i });
        }
      });

      commit('SET_ASSETS', [...otherItems, ...reordered]);

      try {
        await Promise.all(updatedIndexes.map(({ coinId, stockRuId, index }) =>
          changeAssetIndex({ coinId, stockRuId, index })
        ));
      } catch (err) {
        console.error('Failed to sync moved indexes:', err);
      }
    },

    // Вход в редактирование — снимаем черновики долей; выход — сохраняем их.
    // Если доли не сохранились, остаёмся в режиме редактирования, чтобы не потерять ввод.
    async toggleEditing({ state, commit, dispatch }) {
      if (!state.isEditing) {
        commit('SET_DRAFT_WEIGHTS', draftsFromAssets(state.assets));
        commit('SET_WEIGHTS_ERROR', null);
        commit('SET_EDITING_FLAG', true);
        return;
      }
      const isSaved = await dispatch('saveWeights');
      if (isSaved) commit('SET_EDITING_FLAG', false);
    },

    setDraftWeight({ commit }, { coinId, value }) {
      commit('SET_DRAFT_WEIGHT', { coinId, value });
      commit('SET_WEIGHTS_ERROR', null);
    },

    async saveWeights({ state, getters, commit, dispatch }) {
      //Довыполняем отложенные add/delete по debounce — иначе бэк посчитает удалённую монету
      await dispatch('flushPendingAssetSync');
      const { hasInvalid, status } = getters.weightState;
      if (hasInvalid) {
        commit('SET_WEIGHTS_ERROR', 'Доля — число от 0 до 100');
        return false;
      }
      if (status === WEIGHT_STATUS.OVER) {
        commit('SET_WEIGHTS_ERROR', 'Сумма долей больше 100%');
        return false;
      }
      const changed = getters.coinAssets
        .map(item => ({ coin_id: item.coin.api_id, weight: parseWeight(state.draftWeights[item.coin.api_id] ?? '') }))
        .filter((entry, i) => entry.weight !== savedWeight(getters.coinAssets[i]));
      if (!changed.length) return true;

      commit('SET_SAVING_WEIGHTS', true);
      try {
        await saveAssetWeights(changed);
        commit('APPLY_WEIGHTS', changed);
        commit('BUMP_ASSETS_REVISION');
        return true;
      } catch (err) {
        console.error('Ошибка сохранения долей портфеля:', err);
        commit('SET_WEIGHTS_ERROR', err.message);
        return false;
      } finally {
        commit('SET_SAVING_WEIGHTS', false);
      }
    },

    async loadVisibility({ commit }) {
      try {
        const { is_public } = await getPortfolioVisibility();
        commit('SET_PUBLIC', is_public);
        commit('SET_VISIBILITY_LOADED');
        commit('SET_VISIBILITY_ERROR', null);
      } catch (err) {
        console.error('Ошибка загрузки публичности портфеля:', err);
        commit('SET_VISIBILITY_ERROR', 'Не удалось загрузить настройку видимости');
      }
    },

    // Оптимистично переключаем и откатываем при ошибке
    async setVisibility({ state, commit }, isPublic) {
      const previous = state.isPublic;
      commit('SET_PUBLIC', isPublic);
      commit('SET_SAVING_VISIBILITY', true);
      try {
        await patchPortfolioVisibility(isPublic);
        commit('SET_VISIBILITY_ERROR', null);
      } catch (err) {
        console.error('Ошибка сохранения публичности портфеля:', err);
        commit('SET_PUBLIC', previous);
        commit('SET_VISIBILITY_ERROR', 'Не удалось сохранить настройку видимости');
      } finally {
        commit('SET_SAVING_VISIBILITY', false);
      }
    },
  },

  mutations: {
    SET_READY_FLAG(state){ state.isReady = true; },

    SET_EDITING_FLAG(state, status){ state.isEditing = status; },

    SET_ASSETS(state, data){ state.assets = data; },

    SET_LOADING_FLAG(state, status){ state.isLoadingAssets = status; },

    SET_SEARCH_SECTION_OPENED_FLAG(state, status){ state.isCoinSearchOpened = status; },

    SET_ACTIVE_ASSET_TAB(state, tab){ state.activeAssetTab = tab; },

    BUMP_ASSETS_REVISION(state){ state.assetsRevision += 1; },

    TOGGLE_PENDING_ASSET(state, asset) {
      const key = assetKey(asset);
      const index = state.pendingAssets.findIndex(a => assetKey(a) === key);
      if (index !== -1) {
        state.pendingAssets.splice(index, 1);
      } else {
        state.pendingAssets.push(asset);
      }
    },

    CLEAR_PENDING_ASSETS(state) {
      state.pendingAssets = [];
    },

    TOGGLE_ASSET(state, asset) {
      const key = assetKey(asset);
      const foundIndex = state.assets.findIndex(el => itemKey(el) === key);
      if (foundIndex !== -1) {
        state.assets.splice(foundIndex, 1);
        // Реиндексация только элементов того же типа — второй тип не задет
        let i = 0;
        state.assets.forEach(el => {
          if (sameType(el, asset.assetType)) {
            el.index = i++;
          }
        });
      } else {
        const typeCount = state.assets.filter(el => sameType(el, asset.assetType)).length;
        state.assets.push(
          asset.assetType === 'stock_ru'
            ? { index: typeCount, coin: null, stock_ru: asset }
            : { index: typeCount, coin: asset, stock_ru: null }
        );
      }
    },

    SET_DRAFT_WEIGHTS(state, drafts){ state.draftWeights = drafts; },

    SET_DRAFT_WEIGHT(state, { coinId, value }){ state.draftWeights = { ...state.draftWeights, [coinId]: value }; },

    SET_WEIGHTS_ERROR(state, message){ state.weightsError = message; },

    SET_SAVING_WEIGHTS(state, status){ state.isSavingWeights = status; },

    APPLY_WEIGHTS(state, entries) {
      const byCoin = Object.fromEntries(entries.map(entry => [entry.coin_id, entry.weight]));
      state.assets.forEach(item => {
        if (item.coin && item.coin.api_id in byCoin) {
          const weight = byCoin[item.coin.api_id];
          item.weight = weight === null ? null : String(weight);
        }
      });
    },

    SET_PUBLIC(state, status){ state.isPublic = status; },

    SET_VISIBILITY_LOADED(state){ state.isVisibilityLoaded = true; },

    SET_SAVING_VISIBILITY(state, status){ state.isSavingVisibility = status; },

    SET_VISIBILITY_ERROR(state, message){ state.visibilityError = message; },
  },

  getters: {
    sortedAssets(state) {
      return [...state.assets].sort((a, b) => a.index - b.index);
    },
    coinAssets(state) {
      return state.assets.filter(a => a.coin).sort((a, b) => a.index - b.index);
    },
    stockRuAssets(state) {
      return state.assets.filter(a => a.stock_ru).sort((a, b) => a.index - b.index);
    },
    assetsRevision(state) {
      return state.assetsRevision;
    },
    // Доли крипто-активов: в режиме редактирования — по черновикам, иначе — по сохранённым
    weightState(state, getters) {
      const items = getters.coinAssets.map(item => {
        const raw = state.isEditing ? (state.draftWeights[item.coin.api_id] ?? '') : savedWeight(item);
        return { key: item.coin.api_id, raw, weight: parseWeight(raw) };
      });
      const hasInvalid = items.some(item => !isValidWeight(item.raw));
      const valid = items.map(item => ({ key: item.key, weight: Number.isNaN(item.weight) ? null : item.weight }));
      return { items, hasInvalid, ...resolveWeights(valid) };
    },
  }

}

export default Briefcase;
